import { api } from './api';

export interface StandingEntry {
  position: number;
  teamName: string;
  teamId?: string;
  logoPath?: string;
  logoUrl?: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  pointsPerGame?: number;
  qualityOfPlay?: number;
  attScore?: number;
  defScore?: number;
  form: string;
  ppgProgression?: number[];
  isUserTeam?: boolean;
  division?: string;
}

export interface ContextualStanding extends StandingEntry {
  isUserTeam: boolean;
}

export interface StandingsResponse {
  leagueId: string;
  standings: StandingEntry[];
  lastUpdated: string;
}

export interface ContextualStandingsResponse {
  leagueId: string;
  contextualStandings: ContextualStanding[];
  lastUpdated: string;
  unverifiedDeltas?: Record<string, { played: number; won: number; drawn: number; lost: number }>;
  nextMatch?: { opponent: string; isHome: boolean; dateTime: string } | null;
  fullStandings?: StandingEntry[];
}

class StandingsService {
  async getStandings(leagueId: string): Promise<StandingsResponse> {
    const response = await api.get(`/standings/${leagueId}`);
    return response.data.data;
  }

  async getContextualStandings(leagueId: string, userTeams: string[], limit?: number, formLimit?: number, include?: string[]): Promise<ContextualStandingsResponse> {
    const params = new URLSearchParams();
    userTeams.forEach(team => params.append('teams', team));
    if (limit) {
      params.append('limit', limit.toString());
    }
    if (formLimit) {
      params.append('formLimit', formLimit.toString());
    }
    if (include && include.length > 0) {
      params.append('include', include.join(','));
    }

    const response = await api.get(`/standings/${leagueId}/contextual?${params.toString()}`);
    return response.data.data;
  }

  async getBatchContextualStandings(requests: { leagueId: string; teamNames: string[]; limit?: number }[]): Promise<{
    results: {
      leagueId: string;
      contextualStandings: ContextualStanding[];
      lastUpdated: string;
      error?: string;
    }[];
  }> {
    const response = await api.post('/standings/batch', { requests });
    return response.data.data;
  }

  async recalculateStandings(leagueId: string): Promise<void> {
    await api.post(`/standings/${leagueId}/recalculate`);
  }
}

export const standingsService = new StandingsService();
